import { ArrowRight, Check, HelpCircle, Quote, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/seo/SEO";
import { AuroraStage } from "@/components/marketing/AuroraStage";
import { Callout } from "@/components/marketing/Callout";
import { FeatureCell } from "@/components/marketing/FeatureCell";
import { FloatingWords } from "@lyder-talk/ui";
import { LoopDiagram } from "@/components/marketing/LoopDiagram";
import { LoopClockProvider, PracticeLoop } from "@lyder-talk/ui";
import {
  ScrollReveal,
  StaggerReveal,
  StaggerItem,
} from "@/components/marketing/ScrollReveal";
import { SectionHeading } from "@/components/marketing/SectionHeading";
import {
  FEATURES,
  FINAL_CTA,
  HERO_PRIMARY_CTA,
  HERO_SECONDARY_CTA,
  APP_NAME,
} from "@/lib/marketing-constants";
import { usePointerParallax } from "@/hooks/use-pointer-parallax";
import { getAppUrl, getRootUrl } from "@lyder-talk/core";

const TESTIMONIAL = {
  quote:
    "I had been studying French on apps for two years and could barely say a sentence out loud. Six weeks of Lyder Talk and I ordered dinner in Paris without freezing up.",
  name: "Mai Tran",
  role: "Intermediate French learner",
  city: "Da Nang",
};

const REASSURANCE = [
  "No card required",
  "Works in the browser",
  "Five minutes a day",
];

/**
 * The marketing landing page.
 *
 * A signed-in visitor never sees it: `main.tsx` forwards them to the app before
 * React mounts, using the cross-subdomain hint the app publishes
 * (`packages/core/src/lib/session-hint.ts`). The redirect deliberately does not
 * live here — an effect runs after the first paint, which flashed a frame of the
 * hero on the way out.
 */
const Landing = () => {
  const launchApp = () => {
    window.location.href = getAppUrl("/auth/signin");
  };

  return (
    <>
      <SEO
        title={`${APP_NAME} - AI speaking practice that improves with you`}
        description="Pick a topic, record your answer, and get feedback on grammar, vocabulary, and pronunciation. Built for adults learning a language on their own time."
        url="/"
        keywords="language learning, speaking practice, AI tutor, pronunciation, fluency, conversation practice, English speaking, language learning app"
      />

      <LoopClockProvider>
        <div className="bg-background">
          {/* ── HERO ──────────────────────────────────────────────
              The one dark band the site opens on. It carries the
              aurora, the headline, and the loop running live — the
              product's shape before a word of explanation. */}
          <HeroSection onLaunch={launchApp} />

          {/* WHAT YOU GET */}
          <Callout />

          {/* HOW IT WORKS — the loop, drawn as a loop */}
          <section
            aria-labelledby="how-it-works"
            className="relative border-b border-border/60 bg-background"
          >
            <div className="container mx-auto max-w-6xl px-4 py-20 md:py-28">
              <SectionHeading
                eyebrow="How it works"
                title="One loop, five steps, every day"
                description="No setup, no lesson plan, no textbook. Each session is one topic, one answer, and a few minutes of speaking — and the last step feeds the first."
              />
              <LoopDiagram />
            </div>
          </section>

          {/* FEATURES (bento) */}
          <section
            aria-labelledby="features"
            className="border-b border-border/60 bg-slate-50/50 dark:bg-slate-900/30"
          >
            <div className="container mx-auto max-w-6xl px-4 py-20 md:py-28">
              <SectionHeading
                title="A complete practice loop, not a feature checklist"
                description="Speaking, vocabulary, and progress live in the same place. Each one feeds the next."
              />
              <StaggerReveal
                className="mt-12 grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-5"
                stagger={0.08}
              >
                {FEATURES.map((feature) => (
                  <StaggerItem key={feature.title}>
                    <FeatureCell feature={feature} />
                  </StaggerItem>
                ))}
              </StaggerReveal>
            </div>
          </section>

          {/* TESTIMONIAL */}
          <section
            aria-labelledby="testimonial"
            className="border-b border-border/60 bg-background"
          >
            <div className="container mx-auto max-w-3xl px-4 py-20 text-center md:py-28">
              <ScrollReveal>
                <Quote
                  className="mx-auto h-7 w-7 text-primary/40"
                  aria-hidden="true"
                />
                <blockquote
                  id="testimonial"
                  className="mt-6 text-balance font-display text-xl font-medium leading-snug text-foreground sm:text-2xl"
                >
                  {TESTIMONIAL.quote}
                </blockquote>
                <figcaption className="mt-6 text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">
                    {TESTIMONIAL.name}
                  </span>
                  <span className="mx-1.5 text-muted-foreground/60">·</span>
                  {TESTIMONIAL.role}
                  <span className="mx-1.5 text-muted-foreground/60">·</span>
                  {TESTIMONIAL.city}
                </figcaption>
              </ScrollReveal>
            </div>
          </section>

          {/* ── FINAL CTA ─────────────────────────────────────────
              The page ends on the same surface it opened on: dark,
              aurora-lit, one decision left to make. */}
          <section
            aria-labelledby="final-cta"
            className="relative isolate overflow-hidden"
          >
            <AuroraStage fadeBottom />
            <div className="container relative mx-auto max-w-4xl px-4 py-24 text-center md:py-32">
              <ScrollReveal>
                <div className="loop-sheen relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.045] px-6 py-12 shadow-2xl shadow-black/30 backdrop-blur-xl sm:px-12 sm:py-16">
                  <h2
                    id="final-cta"
                    className="text-balance font-display text-3xl font-semibold tracking-tight text-white sm:text-4xl"
                  >
                    {FINAL_CTA.title}
                  </h2>
                  <p className="mx-auto mt-4 max-w-lg text-balance text-base text-white/55 leading-relaxed">
                    {FINAL_CTA.body}
                  </p>

                  <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
                    <Button
                      size="lg"
                      onClick={launchApp}
                      className="group h-11 w-full gap-2 text-base font-semibold sm:w-auto"
                    >
                      {FINAL_CTA.primary}
                      <ArrowRight
                        className="h-4 w-4 transition-transform group-hover:translate-x-0.5"
                        aria-hidden="true"
                      />
                    </Button>
                    <Button
                      variant="ghost"
                      size="lg"
                      onClick={() =>
                        (window.location.href = getRootUrl("/faq"))
                      }
                      className="h-11 w-full gap-2 text-base text-white/60 hover:bg-white/10 hover:text-white sm:w-auto"
                    >
                      <HelpCircle className="h-4 w-4" aria-hidden="true" />
                      {FINAL_CTA.secondary}
                    </Button>
                  </div>

                  <p className="mt-5 text-xs text-white/40">
                    {FINAL_CTA.reassurance}
                  </p>
                  <p className="mt-6 inline-flex items-center gap-1.5 text-xs text-white/40">
                    <Sparkles
                      className="h-3.5 w-3.5 text-secondary"
                      aria-hidden="true"
                    />
                    <span>Currently in open beta.</span>
                  </p>
                </div>
              </ScrollReveal>
            </div>
          </section>
        </div>
      </LoopClockProvider>
    </>
  );
};

/* ── Hero ───────────────────────────────────────────────────── */

function HeroSection({ onLaunch }: { onLaunch: () => void }) {
  const { smoothX, smoothY, onPointerMove, reset } = usePointerParallax();

  return (
    <section
      aria-labelledby="hero-title"
      className="relative isolate -mt-16 overflow-hidden border-b border-border/40 bg-[hsl(224_45%_7%)] text-white"
      onPointerMove={onPointerMove}
      onPointerLeave={reset}
    >
      <AuroraStage fadeBottom />

      {/* The header is a sticky sibling above this section, not an overlay,
          so the hero pulls up under it (-mt-16) and gives the space back
          with pt-16. That is what lets the transparent header sit on the
          aurora instead of on the page background. */}
      <div className="container relative mx-auto max-w-7xl px-4 pb-20 pt-24 sm:pt-24 md:pb-24 md:pt-28">
        <div className="grid items-center gap-14 lg:grid-cols-12 lg:gap-12">
          {/* Copy */}
          <div className="relative z-10 lg:col-span-6">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.06] px-3 py-1.5 backdrop-blur-sm">
              <Sparkles
                className="h-3.5 w-3.5 text-secondary"
                aria-hidden="true"
              />
              <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-white/70">
                AI speaking practice · open beta
              </span>
            </span>

            <h1
              id="hero-title"
              className="mt-6 text-balance font-display text-[2.75rem] font-semibold leading-[1.03] tracking-tight text-white sm:text-6xl lg:text-[4rem]"
            >
              Speak more,
              <br />
              <span className="bg-gradient-to-r from-[hsl(217_91%_78%)] to-[hsl(38_92%_58%)] bg-clip-text text-transparent">
                freeze less.
              </span>
            </h1>

            <p className="mt-5 max-w-xl text-balance text-[17px] leading-relaxed text-white/55">
              Practice that listens back. Pick a topic, answer out loud, and get
              notes on grammar, vocabulary and pronunciation.
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
              <Button
                size="lg"
                onClick={onLaunch}
                className="group h-11 w-full gap-2 text-base font-semibold sm:w-auto"
              >
                {HERO_PRIMARY_CTA}
                <ArrowRight
                  className="h-4 w-4 transition-transform group-hover:translate-x-0.5"
                  aria-hidden="true"
                />
              </Button>
              <Button
                variant="ghost"
                size="lg"
                onClick={() =>
                  window.open(
                    getAppUrl("/auth/signin"),
                    "_blank",
                    "noopener,noreferrer",
                  )
                }
                className="h-11 w-full border border-white/15 text-base text-white/70 hover:bg-white/10 hover:text-white sm:w-auto"
              >
                {HERO_SECONDARY_CTA}
              </Button>
            </div>

            <ul className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-2 text-[13px] text-white/45">
              {REASSURANCE.map((item) => (
                <li key={item} className="flex items-center gap-1.5">
                  <Check
                    className="h-3.5 w-3.5 text-emerald-300/80"
                    aria-hidden="true"
                  />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* The loop, running */}
          <div className="relative z-10 lg:col-span-6">
            <div className="flex items-start gap-5 xl:gap-6">
              <div className="w-full max-w-[400px]">
                <PracticeLoop />
              </div>
              <FloatingWords smoothX={smoothX} smoothY={smoothY} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Landing;
